// services/toastService.ts
import React from 'react';
import { toast, ToastOptions } from 'react-toastify';

/**
 * Maps technical progress steps to user-friendly messages.
 * Industry pattern: Show general progress states rather than technical details.
 * @param technicalStep Technical step from backend
 * @param percentage Current progress percentage
 * @returns User-friendly progress message
 */
const getFriendlyProgressMessage = (technicalStep: string, percentage: number): string => {
    const step = technicalStep?.toLowerCase() || '';
    
    // Early stage (0-30%)
    if (percentage < 30) {
        if (step.includes('cache') || step.includes('checking')) {
            return 'Preparing your export...';
        }
        if (step.includes('preparing') || step.includes('queued')) {
            return 'Preparing your export...';
        }
        return 'Starting export...';
    }
    
    // Middle stage (30-70%)
    if (percentage < 70) {
        if (step.includes('generating') || step.includes('processing')) {
            return 'Processing candidate data...';
        }
        if (step.includes('checksum') || step.includes('validating')) {
            return 'Processing candidate data...';
        }
        return 'Processing candidate data...';
    }
    
    // Final stage (70-100%)
    if (step.includes('upload') || step.includes('storage') || step.includes('saving')) {
        return 'Finalizing export...';
    }
    
    return 'Almost done...';
};

/**
 * Displays an error toast notification.
 * @param message The message to display.
 * @param options Optional ToastOptions to override defaults provided by ToastContainer.
 */
export const showErrorToast = (message: string, options?: ToastOptions) => {
    toast.error(message, options);
};

/**
 * Displays a success toast notification.
 * @param message The message to display.
 * @param options Optional ToastOptions to override defaults provided by ToastContainer.
 */
export const showSuccessToast = (message: string, options?: ToastOptions) => {
    toast.success(message, options);
};

/**
 * Displays an info toast notification.
 * @param message The message to display.
 * @param options Optional ToastOptions to override defaults provided by ToastContainer.
 */
export const showInfoToast = (message: string, options?: ToastOptions) => {
    toast.info(message, options);
};

/**
 * Displays a warning toast notification.
 * @param message The message to display.
 * @param options Optional ToastOptions to override defaults provided by ToastContainer.
 */
export const showWarningToast = (message: string, options?: ToastOptions) => {
    toast.warn(message, options);
};

/**
 * Displays a custom export toast with progress bar and cancel button.
 * @param onCancel Callback function to execute when cancel is clicked.
 * @returns Toast ID for updates/dismissal.
 */
export const showExportToast = (onCancel: () => void) => {
    return toast(
        <div className="flex flex-col gap-3 w-full py-1 select-none">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-1">
                    <svg
                        className="animate-spin h-5 w-5 text-blue-500 flex-shrink-0"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        ></circle>
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 text-sm">Exporting candidates</div>
                        <div className="text-xs text-gray-600 mt-0.5">
                            Preparing your export...
                        </div>
                    </div>
                </div>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onCancel();
                    }}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex-shrink-0"
                    aria-label="Cancel export"
                >
                    Cancel
                </button>
            </div>
            <div className="relative w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                <div
                    className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-full"
                    style={{
                        width: '40%',
                        animation: 'progress-slide 1.5s ease-in-out infinite'
                    }}
                ></div>
            </div>
            <style jsx>{`
                @keyframes progress-slide {
                    0% {
                        transform: translateX(-100%);
                    }
                    100% {
                        transform: translateX(350%);
                    }
                }
            `}</style>
        </div>,
        {
            autoClose: false,
            closeOnClick: false,
            draggable: false,
            closeButton: false,
            position: 'bottom-right',
            className: 'export-toast',
            style: {
                minWidth: '400px',
            }
        }
    );
};

/**
 * Displays a modern export cancelled toast matching the export toast style.
 */
export const showExportCancelledToast = () => {
    return toast(
        <div className="flex items-center gap-3 w-full py-1 select-none">
            <svg
                className="h-5 w-5 text-orange-500 flex-shrink-0"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
            </svg>
            <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 text-sm">Export cancelled</div>
            </div>
        </div>,
        {
            autoClose: 4000,
            closeOnClick: true,
            draggable: true,
            closeButton: true,
            position: 'bottom-right',
            style: {
                minWidth: '320px',
                padding: '12px 16px',
            }
        }
    );
};

/**
 * Updates export toast with progress information.
 * @param toastId The ID of the toast to update.
 * @param percentage Progress percentage (0-100).
 * @param currentStep Current step description.
 * @param onCancel Callback function to execute when cancel is clicked.
 */
export const updateExportProgress = (toastId: any, percentage: number, currentStep: string, onCancel: () => void) => {
    toast.update(toastId, {
        render: (
            <div className="flex flex-col gap-3 w-full py-1 select-none">
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1">
                        <svg
                            className="animate-spin h-5 w-5 text-blue-500 flex-shrink-0"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            ></circle>
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm">
                                Exporting candidates {Math.round(percentage)}%
                            </div>
                            <div className="text-xs text-gray-600 mt-0.5">{getFriendlyProgressMessage(currentStep, percentage)}</div>
                        </div>
                    </div>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onCancel();
                        }}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex-shrink-0"
                        aria-label="Cancel export"
                    >
                        Cancel
                    </button>
                </div>
                <div className="relative w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div
                        className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
                    ></div>
                </div>
            </div>
        ),
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        closeButton: false,
    });
};

/**
 * Updates export toast to show ready state with download button.
 * @param toastId The ID of the toast to update.
 * @param message Success message to display.
 * @param onDownload Callback function to execute when download is clicked.
 */
export const showExportReadyToast = (toastId: any, message: string, onDownload: () => Promise<void>) => {
    // Create a stable reference to the download callback for this specific toast
    const downloadCallbackRef = { current: onDownload };
    
    const DownloadToastContent = () => {
        const [isDownloading, setIsDownloading] = React.useState(false);
        const [hasError, setHasError] = React.useState(false);
        const [isCompleted, setIsCompleted] = React.useState(false);
        
        const handleDownload = async (e: React.MouseEvent) => {
            e.stopPropagation();
            if (isDownloading || isCompleted) return;
            
            setIsDownloading(true);
            setHasError(false);
            
            try {
                // Use the callback specific to THIS toast instance
                await downloadCallbackRef.current();
                setIsDownloading(false);
                setIsCompleted(true);
                
                // Auto-dismiss after successful download
                setTimeout(() => {
                    toast.dismiss(toastId);
                }, 2000);
            } catch (error) {
                setIsDownloading(false);
                setHasError(true);
            }
        };
        
        return (
            <div className="flex items-center justify-between gap-2.5 w-full select-none">
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    <svg
                        className="h-4 w-4 text-green-500 flex-shrink-0"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                        />
                    </svg>
                    <span 
                        className="text-sm text-gray-900 truncate" 
                        title={message}
                    >
                        {message}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {!isCompleted && (
                        <button
                            onClick={handleDownload}
                            disabled={isDownloading}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-300"
                            aria-label="Download export"
                        >
                            {isDownloading ? 'Downloading...' : 'Download'}
                        </button>
                    )}
                    {isCompleted && (
                        <span className="px-3 py-1.5 text-sm font-medium text-green-600">
                            Downloaded ✓
                        </span>
                    )}
                    {hasError && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                toast.dismiss(toastId);
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
                            aria-label="Close"
                        >
                            <svg
                                className="h-4 w-4"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2}
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        );
    };
    
    toast.update(toastId, {
        render: <DownloadToastContent />,
        autoClose: false,
        closeOnClick: false,
        draggable: true,
        closeButton: true,
        position: 'bottom-right',
        style: {
            minWidth: '320px',
            padding: '12px 16px',
        }
    });
};

/**
 * Shows an export error toast with modern styling.
 * @param message Error message to display.
 */
export const showExportErrorToast = (message: string) => {
    return toast(
        <div className="flex items-center gap-2.5 w-full py-1 select-none">
            <svg
                className="h-4 w-4 text-red-500 flex-shrink-0"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
            </svg>
            <span className="text-sm text-gray-900">{message}</span>
        </div>,
        {
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
            closeButton: true,
            position: 'bottom-right',
            style: {
                minWidth: '320px',
                padding: '12px 16px',
            },
        }
    );
};

/**
 * Updates an existing toast with new content.
 * @param toastId The ID of the toast to update.
 * @param message The new message to display.
 * @param type The type of toast ('success' or 'error').
 */
export const updateToast = (toastId: any, message: string, type: 'success' | 'error') => {
    toast.update(toastId, {
        render: message,
        type: type,
        autoClose: 5000,
        closeOnClick: true,
        closeButton: true,
    });
};

/**
 * Dismisses a toast by ID.
 * @param toastId The ID of the toast to dismiss.
 */
export const dismissToast = (toastId: any) => {
    toast.dismiss(toastId);
};

/* ─────────────────────────────────────────────────────────────────────────
 * Enrichment ("find emails") background-job toasts.
 *
 * A bulk enrichment run is driven by the outreach-bulk-enrich async worker and
 * polled by a global tracker, so the toast must survive route changes. These
 * mirror the export toasts' look but speak "finding emails". Campaign-level
 * cancellation is kept in the outreach toolbar, beside the active operation.
 * ──────────────────────────────────────────────────────────────────────── */

type EnrichProgress = {
    status?: 'queued' | 'running' | 'waiting' | 'partial' | 'cancelled' | 'completed' | 'failed';
    total: number;
    done: number;
    remaining?: number;
    found: number;
    notFound?: number;
    failed?: number;
    skipped?: number;
    warning?: string | null;
};

const enrichSpinner = (
    <svg className="animate-spin h-5 w-5 text-blue-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
    </svg>
);

const enrichBody = (label: string, sub: string, pct: number | null) => (
    <div className="flex flex-col gap-3 w-full py-1 select-none">
        <div className="flex items-center gap-3 flex-1">
            {enrichSpinner}
            <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 text-sm">{label}</div>
                <div className="text-xs text-gray-600 mt-0.5">{sub}</div>
            </div>
        </div>
        <div className="relative w-full h-1 bg-gray-200 rounded-full overflow-hidden">
            {pct === null ? (
                <div className="absolute top-0 left-0 h-full w-1/3 bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-full animate-pulse"></div>
            ) : (
                <div
                    className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                ></div>
            )}
        </div>
    </div>
);

/**
 * Opens the enrichment progress toast (indeterminate until the first poll).
 * @param label Headline, e.g. "Finding emails for 42 contacts".
 * @returns Toast ID for updates/dismissal.
 */
export const showEnrichmentToast = (label = 'Finding emails') => {
    return toast(enrichBody(label, 'Queued…', null), {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        closeButton: false,
        position: 'bottom-right',
        className: 'enrichment-toast',
        style: { minWidth: '360px' },
    });
};

/**
 * Updates the enrichment toast with live progress.
 * @param toastId The toast to update.
 * @param p Progress counts from the bulk-status poll.
 * @param label Headline (kept stable across updates).
 */
export const updateEnrichmentProgress = (toastId: any, p: EnrichProgress, label = 'Finding emails') => {
    const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;
    if (p.status === 'waiting') {
        const remaining = p.remaining ?? Math.max(p.total - p.done, 0);
        toast.update(toastId, {
            render: enrichBody(
                `${label} · waiting`,
                p.warning || `${remaining} remaining · retrying automatically`,
                pct
            ),
            autoClose: false,
            closeOnClick: false,
            draggable: false,
            closeButton: false,
        });
        return;
    }
    const outcomes = [`${p.done}/${p.total} checked`, `${p.found} found`];
    if (p.notFound) outcomes.push(`${p.notFound} not found`);
    if (p.failed) outcomes.push(`${p.failed} failed`);
    const sub = outcomes.join(' · ');
    toast.update(toastId, {
        render: enrichBody(`${label} · ${pct}%`, sub, pct),
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        closeButton: false,
    });
};

/**
 * Settles the enrichment toast with distinct completed, resumable, cancelled,
 * and genuinely failed feedback. Partial finds have already been saved.
 */
export const showEnrichmentDoneToast = (
    toastId: any,
    result: {
        status: 'partial' | 'cancelled' | 'completed' | 'failed';
        found: number;
        notFound?: number;
        failed?: number;
        total: number;
        remaining?: number;
        warning?: string | null;
        error?: string | null;
    }
) => {
    const foundMsg =
        result.found > 0
            ? `${result.found} ${result.found === 1 ? 'email' : 'emails'} found`
            : 'No new emails found';
    const remaining = Math.max(result.remaining ?? (result.total - result.found - (result.notFound ?? 0) - (result.failed ?? 0)), 0);
    const render = result.status === 'completed' ? (
        <div className="flex items-center gap-2.5 w-full py-1 select-none">
            <svg className="h-4 w-4 text-green-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
            <span className="text-sm text-gray-900">
                {foundMsg}
                {result.notFound ? ` · ${result.notFound} not found` : ''} — contacts updated.
            </span>
        </div>
    ) : result.status === 'partial' ? (
        <div className="flex items-center gap-2.5 w-full py-1 select-none">
            <svg className="h-4 w-4 text-amber-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-1.5a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM12 15.75h.008v.008H12v-.008Z" />
            </svg>
            <span className="text-sm text-gray-900">
                {remaining.toLocaleString()} still pending{result.found > 0 ? ` · ${foundMsg}` : ''}. Resume when ready.
            </span>
        </div>
    ) : result.status === 'cancelled' ? (
        <div className="flex items-center gap-2.5 w-full py-1 select-none">
            <svg className="h-4 w-4 text-gray-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 9.75h6v4.5H9v-4.5ZM21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
            <span className="text-sm text-gray-900">
                Email finding cancelled{result.found > 0 ? ` · ${foundMsg}` : ''}. {remaining.toLocaleString()} remaining.
            </span>
        </div>
    ) : (
        <div className="flex items-center gap-2.5 w-full py-1 select-none">
            <svg className="h-4 w-4 text-red-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
            <span className="text-sm text-gray-900">
                Email finding stalled{result.found > 0 ? ` (${foundMsg} first)` : ''} — {result.error || 'try again'}.
            </span>
        </div>
    );
    toast.update(toastId, {
        render,
        autoClose: result.status === 'completed' ? 5000 : 8000,
        closeOnClick: true,
        draggable: true,
        closeButton: true,
        style: { minWidth: '320px', padding: '12px 16px' },
    });
};
